import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/lib/utils';
import { CalendarWheels } from './CalendarWheels';
import { ClockMiniIcon, TodayIcon } from './icons';
import { TimeGrid } from './TimeGrid';
import { isReal } from './dateBoxValue';
import type { DateBoxController } from './useDateBox';

/** The calendar + optional time panel + footer inside the DateBox popover. */
export function DateBoxPopover({ c }: { c: DateBoxController }) {
  const { t } = useTranslation('dateTimeField');

  const timeCaption = (label: string): ReactNode => (
    <div className="text-phx-text-2 gap-phx-2_5 text-phx-2xs mb-1.5 flex items-center font-bold tracking-wide uppercase">
      <ClockMiniIcon />
      {label}
    </div>
  );

  const renderTimeGrid = (which: 'f' | 't', label: string, enabled: boolean) => (
    <TimeGrid
      hour={which === 'f' ? c.parts.fH : c.parts.tH}
      minute={which === 'f' ? c.parts.fM : c.parts.tM}
      step={c.timeStep.step}
      minuteCols={c.timeStep.cols}
      hourCols={8}
      minuteExpand={c.minuteExpand}
      compact
      width={180}
      enabled={enabled}
      disabledHint={t('timeHint')}
      hourLabel={t('hour')}
      minuteLabel={t('minute')}
      caption={timeCaption(label)}
      selectMinuteLabel={t('selectMinute')}
      anyMinuteLabel={t('anyMinute')}
      onPickHour={(hour) => c.pickTime(which, hour, null)}
      onPickMinute={(minute) => c.pickTime(which, null, minute)}
    />
  );

  const timePanel = c.withTime ? (
    <div className="border-phx-border ml-0.5 flex items-start gap-[14px] border-l pl-[14px]">
      {c.mode === 'range'
        ? [
            renderTimeGrid('f', t('timeFrom'), isReal(c.parts.from)),
            renderTimeGrid('t', t('timeTo'), isReal(c.parts.to)),
          ]
        : renderTimeGrid('f', t('time'), isReal(c.parts.from))}
    </div>
  ) : null;

  return (
    <>
      <div className={cn(c.withTime && 'flex items-start')}>
        <div className="flex-none">
          <CalendarWheels
            locale={c.locale}
            viewYear={c.viewYear}
            viewMonth={c.viewMonth}
            mode={c.mode}
            from={c.parts.from}
            to={c.parts.to}
            hoverIso={c.hoverIso}
            hoverDir={c.hoverDir}
            infHover={c.infHover}
            nights={c.nights}
            openStart={c.openStart}
            openEnd={c.openEnd}
            showMonthCol={c.showMonthCol}
            showYearCol={c.showYearCol}
            hasDayNote={c.hasDayNote}
            isDisabled={c.isDisabled}
            dayTint={c.dayTint}
            dayNote={c.dayNote}
            onSelectDay={c.selectDay}
            onHoverDay={(iso, dir) => {
              c.setHoverIso(iso);
              c.setHoverDir(dir);
              c.setInfHover(false);
            }}
            onGridEnter={c.cancelHoverClear}
            onGridLeave={c.clearHoverSoon}
            onInfEnter={(dir) => {
              c.cancelHoverClear();
              c.setInfHover(true);
              c.setHoverDir(dir);
            }}
            onInfLeave={() => {
              c.setInfHover(false);
              c.clearHoverSoon();
            }}
            onClickInfinity={c.clickInfinity}
            setView={c.setView}
          />
        </div>
        {timePanel}
      </div>

      <div className="border-phx-border pt-phx-3_5 mt-2 flex items-center border-t">
        <button
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={c.goToday}
          className="text-phx-link hover:bg-phx-surface-2 rounded-phx h-phx-control-sm gap-phx-2_5 text-phx-xs inline-flex cursor-pointer items-center border-0 bg-transparent px-2 font-semibold"
        >
          <TodayIcon />
          {t('today')}
        </button>
        <div className="ml-auto flex items-center gap-1.5">
          {c.hasValue ? (
            <button
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={c.clearVal}
              className="text-phx-text-muted hover:bg-phx-surface-2 rounded-phx h-phx-control-sm text-phx-xs cursor-pointer border-0 bg-transparent px-2 font-medium"
            >
              {t('clear')}
            </button>
          ) : null}
          {c.withTime ? (
            <button
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={c.closePopover}
              className="border-phx-primary bg-phx-primary text-phx-primary-fg rounded-phx h-phx-control-sm text-phx-xs cursor-pointer border px-2.5 font-semibold"
            >
              {t('done')}
            </button>
          ) : null}
        </div>
      </div>
    </>
  );
}
